import { formatDateOrdinal } from '@airship-platform/airship-patterns';
import { CustomerFile } from '@airship-platform/customers-dal/schema';
import { useCallback, useMemo } from 'react';
import { useAppointmentProposalFiles } from '../../../../data-access/appointmentProposalFiles';
import {
  AppointmentProposalOption,
  useAppointmentProposalOptions,
} from '../../../../data-access/appointmentProposalOptions';
import {
  type HelloSignConfiguration,
  useProposalIntegrations,
} from '../../../../data-access/proposalIntegrations';
import { DocumentRowProps } from './DocumentRow';

type DocumentConfig = {
  name: string;
  notAvailableMessage?: string;
  pendingMessage?: string;
  isGenerating?: boolean;
  onGenerate?: (
    appointmentId: string,
    appointmentProposalId: string,
    appointmentProposalOptionId: string
  ) => Promise<unknown>;
};

const HELLO_SIGN_DOCUMENT_COPY: Record<
  HelloSignConfiguration['fileType'],
  Pick<DocumentConfig, 'name' | 'notAvailableMessage' | 'pendingMessage'>
> = {
  COSTCO_HOME_IMPROVEMENT_SALES_CONTRACTS_PDF: {
    name: 'Costco home improvement sales contract',
    notAvailableMessage:
      'Select generate to create and pre-fill the Costco home improvement sales contract. A link to sign the document is going to be sent to your email.',
    pendingMessage:
      'The document has been sent for signatures. Once all parties have signed, the download will become available.',
  },
};

const isPendingDocument = (
  option: AppointmentProposalOption,
  uploadCategory: string
): boolean =>
  uploadCategory === 'COSTCO_HOME_IMPROVEMENT_SALES_CONTRACTS_PDF' &&
  (option?.externalIds?.some(
    (e) => e.name === 'costcoHomeImprovementSalesContractSignatureRequestId'
  ) ??
    false);

export const useDocuments = () => {
  const {
    options,
    isLoading: isGeneratingDocument,
    buildAppointmentProposalOptionDocumentSignature,
  } = useAppointmentProposalOptions();
  const {
    files,
    generateProposalOptionWorkOrder,
    isLoading: isGeneratingWorkOrder,
  } = useAppointmentProposalFiles();
  const { proposalIntegrationsHelloSign } = useProposalIntegrations();

  const handleGenerateWorkOrders = useCallback(
    (appointmentProposalId: string) =>
      generateProposalOptionWorkOrder(appointmentProposalId),
    [generateProposalOptionWorkOrder]
  );

  const handleGenerateDocumentSignature = useCallback(
    (
      appointmentId: string,
      appointmentProposalId: string,
      appointmentProposalOptionId: string,
      configuration: HelloSignConfiguration
    ) =>
      buildAppointmentProposalOptionDocumentSignature(
        appointmentId,
        appointmentProposalId,
        appointmentProposalOptionId,
        {
          templateId: configuration.templateId,
          fileType: configuration.fileType,
          externalIdName: configuration.externalIdName,
          mergeFields: configuration.mergeFields,
          customFields: configuration.customFields,
        }
      ),
    [buildAppointmentProposalOptionDocumentSignature]
  );

  const dynamicDocuments = useMemo<
    Partial<
      Record<
        NonNullable<CustomerFile['metadata']['uploadCategory']>,
        DocumentConfig
      >
    >
  >(() => {
    const latestByFileType = new Map<
      HelloSignConfiguration['fileType'],
      HelloSignConfiguration
    >();
    [...proposalIntegrationsHelloSign]
      .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
      .forEach((integration) => {
        integration.configuration.forEach((config) => {
          if (!latestByFileType.has(config.fileType)) {
            latestByFileType.set(config.fileType, config);
          }
        });
      });

    return Object.fromEntries(
      [...latestByFileType.values()].map((config) => [
        config.fileType,
        {
          ...HELLO_SIGN_DOCUMENT_COPY[config.fileType],
          onGenerate: (
            appointmentId: string,
            appointmentProposalId: string,
            appointmentProposalOptionId: string
          ) =>
            handleGenerateDocumentSignature(
              appointmentId,
              appointmentProposalId,
              appointmentProposalOptionId,
              config
            ),
          isGenerating: isGeneratingDocument,
        },
      ])
    );
  }, [
    proposalIntegrationsHelloSign,
    handleGenerateDocumentSignature,
    isGeneratingDocument,
  ]);

  const documentConfigs = useMemo<
    Partial<
      Record<
        NonNullable<CustomerFile['metadata']['uploadCategory']>,
        DocumentConfig
      >
    >
  >(
    () => ({
      PROPOSAL_WORK_ORDER_PDF: {
        name: 'Work order',
        notAvailableMessage: 'Select generate to create the work order.',
        onGenerate: (_appointmentId, appointmentProposalId) =>
          handleGenerateWorkOrders(appointmentProposalId),
        isGenerating: isGeneratingWorkOrder,
      },
      PROPOSAL_OPTION_ACCEPTED_PDF: {
        name: 'Agreement',
        notAvailableMessage:
          'Once the proposal is signed and accepted, the agreement document will be generated automatically.',
      },
      ...dynamicDocuments,
    }),
    [handleGenerateWorkOrders, isGeneratingWorkOrder, dynamicDocuments]
  );

  const acceptedOptions = useMemo(
    () =>
      options.flatMap((o) =>
        o.acceptedDate !== undefined
          ? [
              {
                appointmentProposalOptionId: o.appointmentProposalOptionId,
                name: o.name,
                status: o.status,
                acceptedDate: o.acceptedDate,
                documents: Object.entries(documentConfigs).map(
                  ([
                    uploadCategory,
                    {
                      name,
                      notAvailableMessage,
                      pendingMessage,
                      onGenerate,
                      isGenerating,
                    },
                  ]): DocumentRowProps => {
                    const file = files.find(
                      (f) =>
                        f.metadata.appointmentProposalOptionId ===
                          o.appointmentProposalOptionId &&
                        f.metadata.uploadCategory === uploadCategory
                    );

                    if (file) {
                      return {
                        name,
                        status: 'ready' as const,
                        generatedAt: file.createdAt
                          ? formatDateOrdinal(file.createdAt)
                          : undefined,
                        url: file.url,
                      };
                    }

                    if (isPendingDocument(o, uploadCategory)) {
                      return {
                        name,
                        statusMessage: pendingMessage,
                        status: 'pending' as const,
                      };
                    }

                    return {
                      name,
                      statusMessage: notAvailableMessage,
                      status: 'not-available' as const,
                      isGenerating,
                      onGenerate: onGenerate
                        ? () =>
                            onGenerate(
                              o.appointmentId,
                              o.appointmentProposalId,
                              o.appointmentProposalOptionId
                            )
                        : undefined,
                    };
                  }
                ),
              },
            ]
          : []
      ),
    [options, documentConfigs, files]
  );

  return { acceptedOptions };
};
